"use client";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Link from "next/link";
import { Tagline } from "@/components/pro-blocks/landing-page/tagline";
import RichText from "@/components/RichText";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";

interface FAQProps {
  sectionId?: string;
  showContactLink?: boolean;
  contactLinkLabel?: string;
  contactHref?: string;
  tagline?: string;
  title?: string;
  titleTag?: "h1" | "h2" | "h3";
  titleClassName?: string;
  description?: string;
  items?: { question: string; answer: string | DefaultTypedEditorState }[];
}

export function FAQ({
  sectionId = "faq",
  showContactLink,
  contactLinkLabel,
  contactHref,
  tagline,
  title,
  titleTag = "h2",
  titleClassName = "heading-lg",
  description,
  items,
}: FAQProps) {
  const TitleTag = titleTag;
  const itemClassName =
    "group transition-colors duration-300 data-[state=open]:bg-[#f1f0ee] rounded-xs px-4";
  const triggerClassName =
    "text-left text-base font-medium transition-colors duration-300 hover:text-foreground/80 data-[state=open]:text-foreground";
  const contentClassName =
    "text-muted-foreground text-base transition-all duration-300 ease-out group-data-[state=closed]:-translate-y-1 group-data-[state=closed]:opacity-0 group-data-[state=open]:translate-y-0 group-data-[state=open]:opacity-100";

  return (
    <section
      id={sectionId}
      className="bg-background section-padding-y"
      aria-labelledby="faq-heading"
    >
      <div className="container-padding-x container mx-auto">
        <div className="flex flex-col gap-10 md:gap-12">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
            {/* Section Header */}
            <div className="section-title-gap-lg flex flex-col items-start text-left gap-">
              {/* Category Tag */}
              <Tagline>{tagline}</Tagline>
              {/* Main Title */}
              <TitleTag id="faq-heading" className={`${titleClassName} text-foreground`}>
                {title}
              </TitleTag>
              {/* Section Description */}
              <p className={`${titleClassName} text-muted-foreground`}>
                {description}
                {showContactLink && contactLinkLabel && contactHref ? (
                  <>
                    {" "}
                    <Link href={contactHref} className="text-primary underline">
                      {contactLinkLabel}
                    </Link>
                  </>
                ) : null}
              </p>
            </div>

            {/* FAQ Accordion */}
            <Accordion
              type="single"
              collapsible
              defaultValue={items?.length ? "item-1" : undefined}
              aria-label="FAQ items"
            >
              {items?.map((item, index) => (
                <AccordionItem
                  key={`${item.question}-${index}`}
                  value={`item-${index + 1}`}
                  className={itemClassName}
                >
                  <AccordionTrigger className={triggerClassName}>
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className={contentClassName}>
                    {typeof item.answer === "string" ? (
                      item.answer
                    ) : (
                      <RichText
                        data={item.answer}
                        enableGutter={false}
                        enableProse={false}
                        className="text-muted-foreground text-base"
                      />
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
